// src/services/aeo/tasks.ts
//
// Delivery > Tasks: the client's Master Tracker sheet in Oura (ourteam-backend
// epic #6480). Staff only. The sheet is the source of truth: a change made in
// Oura is written to the task's row, and when the sheet changed that cell first
// the server answers 409 with the sheet's value, which wins.
//
//   getTasks(businessId)                     GET   /businesses/{id}/tasks
//   linkSheet(businessId, url)               POST  /businesses/{id}/tasks/sheet
//   readSheetNow(businessId)                 POST  /businesses/{id}/tasks/sync
//   patchTask(businessId, taskId, change)    PATCH /businesses/{id}/tasks/{taskId}
//   createTask(businessId, body)             POST  /businesses/{id}/tasks            (ourteam-frontend#4751, backend #6621)
//   deleteTask(businessId, taskId)           DELETE /businesses/{id}/tasks/{taskId}  (ourteam-frontend#4751, backend #6621)
//
// Nothing here throws: a refusal comes back as `{ok: false, message}` with the
// server's sentence, which is the one the screen shows.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;

export interface TaskHistoryEntry {
    field: string;
    before: string | null;
    after: string | null;
    /** Which side the change came from. */
    where: "sheet" | "oura";
    by: string | null;
    at: string | null;
    /** Why an Oura change was dropped ("the sheet changed this cell first"). */
    note: string | null;
}

export interface ApiTask {
    id: string;
    /** The row's place in its own tab. */
    order: number;
    /** The Priority (or ID) cell as written: "3", "M2-4". */
    priority: string;
    action: string;
    codes: string[];
    label: string | null;
    notes: string;
    tool: string;
    gate: string;
    owner: string;
    /** YYYY-MM-DD, or null when the Target cell is not a date (see targetText). */
    target: string | null;
    targetText: string | null;
    status: string;
    result: string;
    changedInSheet: { by: string | null; at: string | null } | null;
    history: TaskHistoryEntry[];
}

export interface ApiTaskPhase {
    key: string;
    name: string;
    sub: string;
    tasks: ApiTask[];
}

export interface TasksPayload {
    sheetUrl: string | null;
    lastReadAt: string | null;
    lastError: string | null;
    phases: ApiTaskPhase[];
    needsCode: string[];
    /** Only on the link's answer. */
    linked?: { converted: boolean; tasks: number; phases: number; problems: string[] };
}

export interface TaskChange {
    status?: string;
    resultAppend?: string;
    /** The whole Result Record, rewritten as typed: no date stamp, "" clears it. Never with resultAppend (ourteam-backend#6692). */
    result?: string;
    target?: string;
    owner?: string;
    notes?: string;
    code?: string;
    /** null clears the label. */
    label?: string | null;
}

/** POST body. `tab` is the sheet's tab title ("M2 KL and PJ"). `idempotencyKey` (1-128 chars) is required: the same key sent
 *  again returns the task it already made, so a retry never adds a second row. */
export interface NewTask {
    tab: string;
    action: string;
    owner?: string;
    target?: string;
    status?: string;
    notes?: string;
    idempotencyKey: string;
}

export type Answer<T> =
    | { ok: true; data: T }
    | {
          ok: false;
          status: number | null;
          message: string;
          /** The refusal slug (`errors[0]`): "bad_target", "sheet_won", "write_failed"... */
          code?: string;
          sheetValue?: string;
          field?: string;
          /** A 409 sheet_won on DELETE: the task as it is now. */
          task?: ApiTask;
      };

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

type ErrorBody = {
    message?: string;
    errors?: string[];
    data?: { field?: string; sheetValue?: string; task?: ApiTask };
};

function refusal<T>(err: unknown, where: string): Answer<T> {
    const res = (err as { response?: { status?: number; data?: ErrorBody } }).response;
    if (!res) return { ok: false, status: null, message: "The server did not answer. Check your connection and try again." };
    if (!res.data?.message) catchErrorLog(`[oura] tasks ${where} failed (${res.status ?? "no status"})`);
    return {
        ok: false,
        status: res.status ?? null,
        message: res.data?.message ?? "That did not go through. Try again.",
        code: res.data?.errors?.[0],
        field: res.data?.data?.field,
        sheetValue: res.data?.data?.sheetValue,
        task: res.data?.data?.task,
    };
}

const base = (businessId: string) => `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/tasks`;

export async function getTasks(businessId: string): Promise<Answer<TasksPayload>> {
    try {
        const res = await axios.get(base(businessId), { headers: authHeaders() });
        return { ok: true, data: res.data?.data as TasksPayload };
    } catch (err) {
        return refusal(err, "read");
    }
}

export async function linkSheet(businessId: string, url: string): Promise<Answer<TasksPayload>> {
    try {
        const res = await axios.post(`${base(businessId)}/sheet`, { url }, { headers: authHeaders() });
        return { ok: true, data: res.data?.data as TasksPayload };
    } catch (err) {
        return refusal(err, "link");
    }
}

export async function readSheetNow(businessId: string): Promise<Answer<TasksPayload>> {
    try {
        const res = await axios.post(`${base(businessId)}/sync`, {}, { headers: authHeaders() });
        return { ok: true, data: res.data?.data as TasksPayload };
    } catch (err) {
        return refusal(err, "read now");
    }
}

export async function patchTask(businessId: string, taskId: string, change: TaskChange): Promise<Answer<ApiTask>> {
    try {
        const res = await axios.patch(`${base(businessId)}/${encodeURIComponent(taskId)}`, change, { headers: authHeaders() });
        return { ok: true, data: res.data?.data as ApiTask };
    } catch (err) {
        return refusal(err, "change");
    }
}

/** 201 with the new task. Never add a row to the screen before this answers. */
export async function createTask(businessId: string, body: NewTask): Promise<Answer<ApiTask>> {
    try {
        const res = await axios.post(base(businessId), body, { headers: authHeaders() });
        return { ok: true, data: res.data?.data as ApiTask };
    } catch (err) {
        return refusal(err, "add");
    }
}

/** 200 {id, archived: true}. Also 200 when the task was already gone. */
export async function deleteTask(businessId: string, taskId: string): Promise<Answer<{ id: string; archived: boolean }>> {
    try {
        const res = await axios.delete(`${base(businessId)}/${encodeURIComponent(taskId)}`, { headers: authHeaders() });
        return { ok: true, data: res.data?.data as { id: string; archived: boolean } };
    } catch (err) {
        return refusal(err, "delete");
    }
}
